import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  useTheme
} from '@mui/material';
import { 
  History as HistoryIcon,
  Download as DownloadIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import SweetAlert from 'sweetalert2';
import { API_URL, createAxiosInstance } from '../../../api';

const HistoryTab = React.memo(({ subscriptionStatus, hasPaid, userId, isActive }) => {
  const theme = useTheme();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [billingHistory, setBillingHistory] = useState([]);
  const [error, setError] = useState(null);

  // Fetch billing history only when tab is active and data hasn't been loaded
  useEffect(() => {
    const fetchBillingHistory = async () => {
      if (!userId || !isActive || dataLoaded) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(`${API_URL}/api/billings/history/${userId}`);
        
        setBillingHistory(response.data.history || []);
        setDataLoaded(true);
        
      } catch (error) {
        console.error('Error fetching billing history:', error);
        setError('Failed to load billing history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBillingHistory();
  }, [userId, isActive, dataLoaded]);

  // Format date
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-UK', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }, []);

  // Get status color
  const getStatusColor = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'succeeded':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  }, []);

  // Handle download invoice
  const handleDownloadInvoice = useCallback(async (invoiceId) => {
    try {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(`${API_URL}/api/billings/invoice/${invoiceId}`, {
        responseType: 'blob'
      });
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      SweetAlert.fire({
        title: 'Error',
        text: 'Failed to download invoice. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '300px' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!hasPaid) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          <AlertTitle>No Subscription</AlertTitle>
          You need an active subscription to view billing history.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>

      {billingHistory.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          height: '300px',
          gap: 2
        }}>
          <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" color="text.secondary">
            No Billing History
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Your billing history will appear here once you have transactions.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.primary.main + '08' }}>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billingHistory.map((entry, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: theme.palette.primary.main + '04' 
                    } 
                  }}
                >
                  <TableCell>
                    {formatDate(entry.periodStart)}
                  </TableCell>
                  <TableCell>
                    {entry.description || 'Subscription Payment'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    £{entry.amountPaid || entry.amount || '0.00'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={entry.status || 'Unknown'}
                      color={getStatusColor(entry.status)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {entry.invoiceId && (
                      <Box
                        component="button"
                        onClick={() => handleDownloadInvoice(entry.invoiceId)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          border: 'none',
                          backgroundColor: 'transparent',
                          color: theme.palette.primary.main,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.primary.main + '08'
                          },
                          p: 0.5,
                          borderRadius: 1
                        }}
                      >
                        <DownloadIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption">
                          Download
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
});

HistoryTab.displayName = 'HistoryTab';

export default HistoryTab;
